import React, { Dispatch, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useReactToPrint } from "react-to-print";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Button, Divider, IconButton, Stack, styled, Typography } from "@mui/material";
import { Editor } from "@tiptap/core";
import { VisibilityLock } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { FilesPickerModal } from "components/modals/Files-picker/FilesPicker";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionType } from "constants/documents";
import { useDocumentSocketHook, useFormatDateHook, useNavigate } from "hooks";
import { RichTextEditorProvider } from "mui-tiptap";
import { useDocumentSelect, useViewActions } from "store";
import { Document } from "store/types";

import { RoleName } from "../../../constants";
import { DragDrop } from "../Drag-drop/DragDrop";
import { ReportQueryManager } from "../Report-query-manager/ReportQueryManager";
import { Section } from "../Sections";
import { AddSectionToolbar } from "./AddSectionToolbar";
import { ContentContext } from "./content-context";
import { TextSectionToolbar } from "./TextSectionToolbar";
import { TopHead } from "./TopHead";
import { useContentContextHook } from "./use-content-context-hook";
import { useInlineMeasureTooltip } from "./use-inline-measure-tooltip";
import { useSectionsDrag } from "./useSectionsDrag";

export const sectionTypesFile = [DocumentSectionType.AIFile, DocumentSectionType.File, DocumentSectionType.Image];
export const sectionTypesQuestions = [DocumentSectionType.Question, DocumentSectionType.AIQuestion];
export const reportSections = [
  DocumentSectionType.KPI,
  DocumentSectionType.Graph,
  DocumentSectionType.Table,
  DocumentSectionType.CashflowGraph
];
export type ReportSectionType = (typeof reportSections)[number];
// Add new styled component for selection overlay
const SelectionBox = styled("div")({
  position: "absolute",
  border: "1px solid #0078D7",
  backgroundColor: "rgba(0, 120, 215, 0.1)",
  pointerEvents: "none",
  zIndex: 1000
});

export interface DocumentProps {
  document: Document<true>;
  setDocument: Dispatch<React.SetStateAction<Document<true>>>;
  onShowSharePopup: (section: number) => void;
  onSelectSection: (section: number, selected: boolean) => void;
  selectedSections: number[];
  setSelectionMode: (mode: boolean) => void;
  selectionMode: boolean;
  userId: number;
  containerRef: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

export const Content: React.FC<DocumentProps> = ({
  document,
  selectionMode,
  selectedSections,
  setSelectionMode,
  onShowSharePopup,
  onSelectSection,
  userId,
  setDocument,
  containerRef,
  onClose
}) => {
  const navigate = useNavigate();
  const { sections, deal_title, created_at, deal_id, id, title } = document;

  // Get is_private from store to ensure it's always up to date
  const isPrivate = useDocumentSelect(document.id)?.is_private ?? document.is_private;

  const [currentEditor, setCurrentEditor] = useState<Editor | null>(null);
  const [currentSectionId, setCurrentSectionId] = useState<number | null>(null);
  const [currentSectionIsRtl, setCurrentSectionIsRtl] = useState<boolean | null>(null);

  useDocumentSocketHook({ documentId: document.id, userId, dealId: document.deal_id, setDocument });

  const hasReportSection = sections.some((section) => reportSections.includes(section.type));

  const sectionsNumber = sections.length;

  const { setSelectedQuestionAction, setShareDialog } = useViewActions();
  const formatDate = useFormatDateHook();
  const reactToPrintFn = useReactToPrint({ contentRef: containerRef });

  useInlineMeasureTooltip();

  const value = useContentContextHook({
    document,
    containerRef,
    setDocument
  });

  const {
    isSelecting,
    selectionStyle,
    handleDrag,
    handleDragStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    reorderSections,
    isSectionMarkedForDrag
  } = useSectionsDrag({
    sections,
    documentId: document.id,
    setDocument
  });

  const { isEnterDisabled, handleDrop, handleHTMLDrop, handlePaste, handleContainerClick, filesPickerParams } = value;

  useEffect(() => {
    window.document.addEventListener("paste", handlePaste);
    return () => {
      window.document.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);

  // Clear toolbar only when focus moves to a *competing* text input (question input, ChatInput,
  // title, sidebar search, etc.). We do NOT clear when focus moves to toolbar buttons, dropdown
  // menus, or portals - those are part of the editing session even though they render outside the
  // document container. The core rule: clear when user focuses a text input that doesn't use our toolbar.
  useEffect(() => {
    const isTextInputLike = (el: HTMLElement) => {
      if (el.tagName === "TEXTAREA") return true;
      if (el.tagName === "INPUT") {
        const type = (el as HTMLInputElement).type;
        return !["button", "submit", "reset", "checkbox", "radio", "hidden"].includes(type);
      }
      return el.isContentEditable;
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Don't clear when clicking toolbar buttons
      const isToolbarButton = target.closest(".MuiIconButton-root") || target.closest(".MuiButtonBase-root");
      if (isTextInputLike(target) && !target.closest(".tiptap") && !isToolbarButton) {
        setCurrentEditor(null);
        setCurrentSectionId(null);
      }
    };
    const doc = window.document;
    doc.addEventListener("focusin", handleFocusIn);
    return () => doc.removeEventListener("focusin", handleFocusIn);
  }, []);

  //invalidate selected question on unmount, this flag use for save selected question to store
  useEffect(() => {
    return () => {
      setSelectedQuestionAction(null);
    };
  }, []);

  useEffect(() => {
    // prevent multiple Enter key presses for text section
    //enter press is used for create new section and cut some text from previous one
    if (isEnterDisabled) {
      const preventEnterKey = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      };
      containerRef.current?.addEventListener("keydown", preventEnterKey, true);
      return () => containerRef.current?.removeEventListener("keydown", preventEnterKey, true);
    }
  }, [isEnterDisabled]);

  useEffect(() => {
    // if no sections, create initial section
    if (!sections?.length) {
      value.createSection({
        position: 0,
        type: DocumentSectionType.Text,
        text: ""
      });
    }
  }, [sections]);

  return (
    <ContentContext.Provider
      value={{
        ...value,
        handleMultiShare: () => setSelectionMode(true),
        handleShare: onShowSharePopup,
        handleSelectSection: onSelectSection,
        setSelectionMode,
        selectionMode,
        selectedSections,
        reorderSections,
        isSectionMarkedForDrag,
        setCurrentEditor,
        currentSectionId,
        setCurrentSectionId,
        sectionsNumber,
        currentSectionIsRtl,
        setCurrentSectionIsRtl
      }}
    >
      <TopHead id={id} title={title} onClose={onClose} />
      <Stack
        sx={{
          p: "8px 16px",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap: "10px",
          borderTop: "1px solid #DBE8F5",
          borderBottom: "1px solid #DBE8F5",
          mb: "16px"
        }}
      >
        <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center" }}>
          {deal_title && (
            <Stack sx={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
              <LocationCityIcon />
              <Typography variant="body1" sx={{ color: "#496682", fontWeight: "500" }}>
                Deal:
              </Typography>
              <MaterialTooltip title={deal_title}>
                <Typography
                  noWrap
                  onClick={() => navigate({ route: "dealDashboard", params: { deal_id: String(deal_id) } })}
                  variant="body1"
                  sx={{ color: "#26A6AE", fontWeight: "400", cursor: "pointer" }}
                >
                  {deal_title}
                </Typography>
              </MaterialTooltip>
            </Stack>
          )}
          <Stack sx={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
            <TodayOutlinedIcon />
            <Typography variant="body1" sx={{ color: "#496682", fontWeight: "500" }}>
              Add Date:
            </Typography>
            <Typography variant="body1" sx={{ color: "#496682", fontWeight: "400" }}>
              {formatDate({ date: created_at })}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center" }}>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            {deal_id && (
              <Button
                sx={{ padding: "0px 8px", minWidth: "85px", maxWidth: "85px", height: "24px", minHeight: "24px" }}
                variant="outlined"
                startIcon={<PersonAddAltIcon />}
                onClick={() =>
                  setShareDialog({
                    elements: [id],
                    type: "document_id",
                    shareName: title,
                    dealId: deal_id,
                    itemToShare: null
                  })
                }
              >
                Share
              </Button>
            )}
          </RoleComponentGuard>
          <MaterialTooltip isShow title="Print">
            <IconButton onClick={reactToPrintFn}>
              <LocalPrintshopOutlinedIcon sx={{ fill: "#071F35" }} />
            </IconButton>
          </MaterialTooltip>
        </Stack>
      </Stack>
      <Box
        ref={containerRef}
        onMouseDown={(e) => handleMouseDown(e, containerRef)}
        onMouseMove={(e) => handleMouseMove(e, containerRef)}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={(e) => handleContainerClick(e, containerRef)}
        onContextMenu={(e) => e.preventDefault()}
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          alignItems: "center",
          flexGrow: 1,
          bgcolor: "#f7f9fc",
          position: "relative",
          userSelect: "none",
          mb: "16px"
        }}
      >
        {isPrivate && <VisibilityLock sx={{ position: "absolute", right: "55px", top: "23px", fill: "#BAC7CE" }} />}
        <Stack
          sx={{
            maxWidth: "1000px",
            minWidth: "1000px",
            "@media (max-width: 1366px)": { maxWidth: "850px", minWidth: "850px" },
            "@media (max-width: 850px)": { maxWidth: "100%", minWidth: "100%" },
            margin: "auto",
            position: "relative",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              bgcolor: "#dbe8f5",
              px: "15px",
              height: "41px",
              minHeight: "41px",
              flexShrink: 0,
              boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
              borderRadius: "8px 8px 0 0",
              zIndex: 1300
            }}
          >
              <Stack direction="row" alignItems="center" gap="10px">
                <RichTextEditorProvider editor={currentEditor || null}>
                  <TextSectionToolbar />
                </RichTextEditorProvider>
                <Divider orientation="vertical" sx={{ borderColor: "#92a7bc", height: "20px" }} />
              </Stack>
              <AddSectionToolbar sections={sections} documentDealId={document.deal_id} />
          </Stack>
        </Stack>
        <DragDrop
          dropId="document"
          onFilesDrop={handleDrop}
          onHTMLDrop={handleHTMLDrop}
          containerSx={{
            flexGrow: 1,
            alignItems: "stretch",
            p: "16px 15px 20px 15px",
            maxWidth: "1000px",
            minWidth: "1000px",
            "@media (max-width: 1366px)": { maxWidth: "850px", minWidth: "850px" },
            "@media (max-width: 850px)": { maxWidth: "100%", minWidth: "100%" },
            borderRadius: "0 0 8px 8px",
            backgroundColor: "#fff",
            margin: "auto",
            boxShadow: " 0 1px 2px 0 rgba(18, 18, 23, 0.10)",
            gap: "15px"
            // minHeight: "100%"
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1300,
              width: "100%",
              bgcolor: "#dbe8f5",
              borderRadius: "3px 3px 0 0",
            }}
          > 
            {hasReportSection && document.report_data?.queries && (
              <ReportQueryManager {...document.report_data.queries} />
            )}
          </Box>
          <DragDropContext onDragEnd={handleDrag} onDragStart={handleDragStart}>
            <Droppable droppableId="document" type="STAGE">
              {(provided) => (
                <Stack ref={provided.innerRef} {...provided.droppableProps} gap="15px">
                  {sections.map((section, index) => (
                    <Draggable key={section.id} draggableId={`${section.id}`} index={index}>
                      {(draggableProvided) => (
                        <Section
                          key={section.id}
                          section={section}
                          draggableProvided={draggableProvided}
                          documentDealId={document.deal_id}
                          index={index}
                          questions={document.questions.filter((question) => question.section_id === section.id)}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
          {isSelecting && <SelectionBox style={selectionStyle} />}
        </DragDrop>
      </Box>
      {filesPickerParams && <FilesPickerModal {...filesPickerParams} />}
    </ContentContext.Provider>
  );
};
